/* eslint-disable no-undef */
import { useState, useEffect, useCallback, useRef } from "react";
import { useTheme } from "../context/ThemeContext";
import {
  useLanguage,
  getLang,
  getLocale,
  formatMoney,
  getCurrencySymbol
} from "../lang/useLanguage";
import PaginationBar from "../components/PaginationBar";

const BomTree = ({ productCode, t }) => {
  const p = getLang().productsPage;
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(false);
  const [assemblyLoading, setAssemblyLoading] = useState(false);
  const [assemblyQty, setAssemblyQty] = useState(1);
  const [assemblyResult, setAssemblyResult] = useState(null);

  useEffect(() => {
    if (!productCode) return;
    setLoading(true);
    fetch(`${window.location.protocol}//${window.location.hostname}/api/bom/${productCode}/tree`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
    })
      .then(r => r.json())
      .then(d => { setTree(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [productCode]);

  const createAssemblyOrder = async () => {
    setAssemblyLoading(true);
    setAssemblyResult(null);
    try {
      const res = await fetch(`${window.location.protocol}//${window.location.hostname}/api/work-orders/assembly`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}`, "Content-Type": "application/json" },
        body: JSON.stringify({ product_code: productCode, quantity: assemblyQty })
      });
      const d = await res.json();
      setAssemblyResult(d);
    } catch (e) { setAssemblyResult({ error: e.message }); }
    setAssemblyLoading(false);
  };

  const NodeRow = ({ node, depth = 0 }) => {
    const [open, setOpen] = useState(true);
    const typeColors = { raw: "#10b981", purchased: "#a78bfa", semi: "#f59e0b", sub_assembly: "#3b82f6" };
    const TYPE_LABELS = {
      raw: p.raw,
      purchased: p.purchasedMaterial,
      semi: p.semiFinished,
      sub_assembly: p.subAssembly
    };
    const color = typeColors[node.material_type] || "#94a3b8";
    const shortage = node.stock_qty < node.quantity_required;
    return (
      <>
        <tr style={{ background: depth % 2 === 0 ? t.bg.card : t.bg.hover }}>
          <td style={{ padding: "8px 12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: depth * 24 }}>
              {node.children?.length > 0 && (
                <button onClick={() => setOpen(!open)} style={{ background: "none", border: "none", cursor: "pointer", color: t.text.muted, fontSize: 16, padding: 0 }}>
                  {open ? "▼" : "▶"}
                </button>
              )}
              {!node.children?.length && <span style={{ width: 20 }} />}
              <span style={{ fontSize: 18 }}>{node.is_sub_assembly ? "🔧" : node.material_type === "raw" ? "🪨" : "📦"}</span>
              <span style={{ fontSize: 13, fontWeight: node.is_sub_assembly ? 700 : 400, color: t.text.primary }}>{node.material_name}</span>
              {node.material_code && <span style={{ fontSize: 11, color: t.text.muted }}>({node.material_code})</span>}
            </div>
          </td>
          <td style={{ padding: "8px 12px", textAlign: "center" }}>
            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: color + "22", color }}>{TYPE_LABELS[node.material_type] || node.material_type}</span>
          </td>
          <td style={{ padding: "8px 12px", textAlign: "right", fontSize: 13, fontWeight: 700, color: t.text.primary }}>{node.quantity_required} {node.unit}</td>
          <td style={{ padding: "8px 12px", textAlign: "right", fontSize: 13, color: shortage ? "#ef4444" : "#10b981", fontWeight: 700 }}>
            {node.stock_qty} {shortage ? "⚠️" : "✅"}
          </td>
          <td style={{ padding: "8px 12px", textAlign: "right", fontSize: 13, color: t.text.secondary }}>{formatMoney(node.unit_cost * node.quantity_required)}</td>
        </tr>
        {open && node.children?.map((child, i) => <NodeRow key={i} node={child} depth={depth + 1} />)}
      </>
    );
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: t.text.muted }}>⏳ {p.loading}</div>;
  if (!tree) return <div style={{ padding: 40, textAlign: "center", color: t.text.muted }}>{p.bomDataNotFound}</div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: t.text.primary }}>🌳 {p.bomTree} — {tree.product_name}</div>
          <div style={{ fontSize: 12, color: t.text.muted, marginTop: 4 }}>{p.totalMaterialCost}: <strong style={{ color: "#10b981" }}>{formatMoney(tree.total_material_cost || 0)}</strong></div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="number" value={assemblyQty} onChange={e => setAssemblyQty(parseInt(e.target.value) || 1)} min="1" style={{ width: 70, padding: "6px 10px", borderRadius: 8, border: "1px solid #334155", background: t.bg.input, color: t.text.primary, fontSize: 13 }} />
          <button onClick={createAssemblyOrder} disabled={assemblyLoading} style={{ padding: "8px 16px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
            {assemblyLoading ? "⏳..." : p.createAssemblyWorkOrder}
          </button>
        </div>
      </div>

      {assemblyResult && (
        <div style={{ marginBottom: 16, padding: "12px 16px", borderRadius: 10, background: assemblyResult.error ? "#7f1d1d22" : "#064e3b22", border: `1px solid ${assemblyResult.error ? "#ef4444" : "#10b981"}` }}>
          {assemblyResult.error ? (
            <div style={{ color: "#ef4444" }}>❌ {assemblyResult.error}</div>
          ) : (
            <div>
              <div style={{ color: "#10b981", fontWeight: 700, marginBottom: 8 }}>✅ {assemblyResult.message}</div>
              <div style={{ fontSize: 13, color: t.text.secondary }}>{p.mainWorkOrder}: <strong>{assemblyResult.main_order_no}</strong></div>
              {assemblyResult.sub_orders?.map((s, i) => (
                <div key={i} style={{ fontSize: 12, color: t.text.muted, marginTop: 4 }}>↳ {s.order_no} — {s.product_code} × {s.quantity}</div>
              ))}
            </div>
          )}
        </div>
      )}

      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: t.bg.hover }}>
            {[
              p.materialColumn,
              p.type,
              p.quantity,
              p.stock,
              p.cost
            ].map(h => (
              <th key={h} style={{ padding: "10px 12px", fontSize: 11, fontWeight: 700, color: t.text.muted, textTransform: "uppercase", textAlign: h === p.materialColumn ? "left" : "right", borderBottom: `1px solid ${t.border.divider}` }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tree.tree?.map((node, i) => <NodeRow key={i} node={node} depth={0} />)}
        </tbody>
      </table>
    </div>
  );
};

const API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") ? "http://localhost:8001" : `${window.location.protocol}//${window.location.hostname}/api`;
const token = () => localStorage.getItem("token");
const headers = () => ({ Authorization: `Bearer ${token()}`, "Content-Type": "application/json" });

const TYPE_CFG = {
  manufactured: { label: getLang().productsPage.ownProduction, color: "#3b82f6", icon: "🏭" },
  purchased:    { label: getLang().productsPage.externallyPurchased, color: "#a78bfa", icon: "🛒" },
  set:          { label: "Set/Kit", color: "#f59e0b", icon: "📦" },
};


const normCode = (v) => String(v || "")
  .toLowerCase()
  .replace(/ı/g, "i")
  .replace(/ğ/g, "g")
  .replace(/ü/g, "u")
  .replace(/ş/g, "s")
  .replace(/ö/g, "o")
  .replace(/ç/g, "c")
  .replace(/[^a-z0-9]/g, "");

const MATERIAL_TYPE_CFG = {
  raw:       { labelKey: "raw",              color: "#10b981" },
  purchased: { labelKey: "purchasedPart",    color: "#a78bfa" },
  semi:      { labelKey: "semiFinishedType", color: "#f59e0b" },
};

const ProductFormModal = ({ existing, onClose, onSaved, t, products }) => {
  const p = getLang().productsPage;
  const [form, setForm] = useState({
    product_code: existing?.product_code || "",
    product_name: existing?.product_name || "",
    product_type: existing?.product_type || "manufactured",
    product_category: existing?.product_category || "mamul",
    is_sales_item: existing?.is_sales_item ?? 1,
    requires_assembly: existing?.requires_assembly ?? 0,
    requires_qc: existing?.requires_qc ?? 1,
    requires_inprocess_qc: existing?.requires_inprocess_qc ?? 1,
    requires_final_qc: existing?.requires_final_qc ?? 1,
    can_be_stocked: existing?.can_be_stocked ?? 1,
    category: existing?.category || "",
    unit: existing?.unit || "adet",
    description: existing?.description || "",
    drawing_no: existing?.drawing_no || "",
    revision: existing?.revision || "A",
    weight_kg: existing?.weight_kg != null ? existing.weight_kg : "",
    material: existing?.material || "",
    standard_time_min: existing?.standard_time_min != null ? existing.standard_time_min : "",
    unit_cost: existing?.unit_cost != null ? existing.unit_cost : "",
    sale_price: existing?.sale_price || "",
    min_stock: existing?.min_stock || 0,
    reorder_quantity: existing?.reorder_quantity || 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const applyCategoryRules = (category) => {
    const rules = {
      mamul: {
        is_sales_item: 1,
        requires_assembly: 0,
        requires_qc: 1,
        requires_inprocess_qc: 0,
        requires_final_qc: 1,
        can_be_stocked: 1,
      },
      yari_mamul: {
        requires_assembly: 0,
        requires_qc: 1,
        requires_inprocess_qc: 1,
        requires_final_qc: 0,
        can_be_stocked: 1,
      },
      islenmis_parca: {
        requires_assembly: 0,
        requires_qc: 1,
        requires_inprocess_qc: 1,
        requires_final_qc: 0,
        can_be_stocked: 1,
      },
      hammadde: {
        requires_assembly: 0,
        requires_qc: 0,
        requires_inprocess_qc: 0,
        requires_final_qc: 0,
        can_be_stocked: 1,
      },
      sarf: {
        is_sales_item: 0,
        requires_assembly: 0,
        requires_qc: 0,
        requires_inprocess_qc: 0,
        requires_final_qc: 0,
        can_be_stocked: 1,
      },
      teknik_servis: {
        is_sales_item: 0,
        requires_assembly: 0,
        requires_qc: 0,
        requires_inprocess_qc: 0,
        requires_final_qc: 0,
        can_be_stocked: 0,
      },
      ticari_urun: {
        requires_assembly: 0,
        requires_qc: 0,
        requires_inprocess_qc: 0,
        requires_final_qc: 0,
        can_be_stocked: 1,
      },
      satin_alinan_parca: {
        requires_assembly: 0,
        requires_qc: 0,
        requires_inprocess_qc: 0,
        requires_final_qc: 0,
        can_be_stocked: 1,
      },
      hurda_fire: {
        requires_assembly: 0,
        requires_qc: 0,
        requires_inprocess_qc: 0,
        requires_final_qc: 0,
        can_be_stocked: 1,
      },
    };

    setForm(prev => ({
      ...prev,
      product_category: category,
      ...(rules[category] || {}),
      product_type:
        ["hammadde", "sarf", "ticari_urun", "satin_alinan_parca"].includes(category)
          ? "purchased"
          : prev.product_type
    }));
  };

  const handleSave = async () => {
    if (!form.product_code || !form.product_name) { setError(p.productNameRequired); return; }
    setLoading(true); setError("");
    try {
      const url = existing ? `${API}/product-cards/${existing.product_code}` : `${API}/product-cards`;
      const method = existing ? "PUT" : "POST";
      const res = await fetch(url, {
        method, headers: headers(),
        body: JSON.stringify({
          ...form,
          weight_kg: form.weight_kg ? parseFloat(form.weight_kg) : null,
          standard_time_min: parseFloat(form.standard_time_min) || 0,
          unit_cost: parseFloat(form.unit_cost) || 0,
          sale_price: parseFloat(form.sale_price) || 0,
          min_stock: parseInt(form.min_stock) || 0,
          reorder_quantity: parseInt(form.reorder_quantity) || 0,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || p.error);
      onSaved();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const I = { width: "100%", padding: "9px 12px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none", boxSizing: "border-box" };
  const L = { display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: t.bg.overlay, backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 20, width: "100%", maxWidth: 640, maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: t.shadow.modal }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 28px", borderBottom: `1px solid ${t.border.default}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: t.bg.header }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: t.text.primary }}>{existing ? `✏️ ${p.editProductCard}` : `╔ ${p.newProductCard.replace(/^\+\s*/, "")}`}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: t.text.muted, cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 28 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div style={{ position: "relative" }}><label style={L}>{p.productCode} *</label>
              <input value={form.product_code} onChange={e => {
                const val = e.target.value;
                setForm(p => ({ ...p, product_code: val }));
                if (val.length > 1) {
                  const q = val.toLowerCase().replace(/[-\s]/g, "");
                  const matches = products.filter(p =>
                    p.product_code?.toLowerCase().replace(/[-\s]/g, "").includes(q) ||
                    p.product_name?.toLowerCase().replace(/[-\s]/g, "").includes(q)
                  ).slice(0, 6);
                  setForm(p => ({ ...p, _suggestions: matches }));
                } else {
                  setForm(p => ({ ...p, _suggestions: [] }));
                }
              }} style={I} placeholder="PRD-001" />
              {(form._suggestions || []).length > 0 && (
                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: t.bg.modal, border: `1px solid ${t.border.input}`, borderRadius: 8, zIndex: 100, maxHeight: 200, overflowY: "auto", boxShadow: t.shadow.modal }}>
                  {form._suggestions.map((p, i) => (
                    <div key={i} onClick={() => setForm(prev => ({ ...prev,
                      product_code: p.product_code, product_name: p.product_name,
                      category: p.category || prev.category,
                      unit: p.unit || prev.unit,
                      weight_kg: p.weight_kg || prev.weight_kg,
                      material: p.material || prev.material,
                      standard_time_min: p.standard_time_min || prev.standard_time_min,
                      unit_cost: p.unit_cost || prev.unit_cost,
                      sale_price: p.sale_price || prev.sale_price,
                      _suggestions: []
                    }))}
                      style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13, color: t.text.primary, borderBottom: `1px solid ${t.border.subtle}`, display: "flex", justifyContent: "space-between" }}
                      onMouseEnter={e => e.currentTarget.style.background = t.bg.toggle}
                      onMouseLeave={e => e.currentTarget.style.background = "none"}>
                      <span style={{ fontWeight: 700 }}>{p.product_code}</span>
                      <span style={{ color: t.text.muted, fontSize: 12 }}>{p.product_name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label style={L}>{p.productType}</label>
              <select value={form.product_type} onChange={e => setForm(p => ({ ...p, product_type: e.target.value }))} style={I}>
                <option value="manufactured">🏭 {p.ownProduction}</option>
                <option value="purchased">🛒 {p.externallyPurchased}</option>
                <option value="set">📦 Set/Kit</option>
              </select>
            </div>

            <div>
              <label style={L}>{p.productCategory}</label>
              <select value={form.product_category} onChange={e => applyCategoryRules(e.target.value)} style={I}>
                <option value="mamul">{p.categoryFinished}</option>
                <option value="yari_mamul">{p.categorySemiFinished}</option>
                <option value="islenmis_parca">{p.categoryMachinedPart}</option>
                <option value="hammadde">{p.categoryRawMaterial}</option>
                <option value="satin_alinan_parca">{p.purchasedPart}</option>
                <option value="ticari_urun">{p.categoryCommercialProduct}</option>
                <option value="sarf">{p.categoryConsumable}</option>
              </select>
            </div>

            <div style={{ gridColumn: "1/-1", display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, padding: 12, border: `1px solid ${t.border.input}`, borderRadius: 12, background: t.bg.card }}>
              {[
                ["is_sales_item", p.showInSales],
                ["requires_assembly", p.requiresAssembly],
                ["requires_qc", "Genel KK"],
                ["requires_inprocess_qc", p.inProcessQc],
                ["requires_final_qc", "Final KK"],
                ["can_be_stocked", p.stockable],
              ].map(([key, label]) => (
                <label key={key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 700, color: t.text.secondary, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={Number(form[key] ?? 0) === 1}
                    onChange={e => setForm(p => ({ ...p, [key]: e.target.checked ? 1 : 0 }))}
                  />
                  {label}
                </label>
              ))}
            </div>
            <div style={{ gridColumn: "1/-1" }}><label style={L}>{p.productName} *</label><input value={form.product_name} onChange={e => setForm(p => ({ ...p, product_name: e.target.value }))} style={I} placeholder={p.productNamePlaceholder} /></div>
            <div><label style={L}>{p.category}</label><input value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} style={I} placeholder={p.categoryPlaceholder} /></div>
            <div>
              <label style={L}>{p.unit}</label>
              <select value={form.unit} onChange={e => setForm(p => ({ ...p, unit: e.target.value }))} style={I}>
                {["adet","kg","mt","lt","m2","set","takım","paket"].map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div><label style={L}>{p.drawingNo}</label><input value={form.drawing_no} onChange={e => setForm(p => ({ ...p, drawing_no: e.target.value }))} style={I} placeholder="CZ-001" /></div>
            <div><label style={L}>{p.revision}</label><input value={form.revision} onChange={e => setForm(p => ({ ...p, revision: e.target.value }))} style={I} placeholder="A" /></div>
            <div><label style={L}>{p.weight} (kg)</label><input type="number" value={form.weight_kg} onChange={e => setForm(p => ({ ...p, weight_kg: e.target.value }))} style={I} min="0" step="0.001" /></div>
            <div><label style={L}>{p.material}</label><input value={form.material} onChange={e => setForm(p => ({ ...p, material: e.target.value }))} style={I} placeholder={p.materialPlaceholder} /></div>
            <div><label style={L}>{p.standardTime} (dk)</label><input type="number" value={form.standard_time_min} onChange={e => setForm(p => ({ ...p, standard_time_min: e.target.value }))} style={I} min="0" step="0.1" /></div>
            <div><label style={L}>{p.unitCost} ({getCurrencySymbol()})</label><input type="number" value={form.unit_cost} onChange={e => setForm(p => ({ ...p, unit_cost: e.target.value }))} style={I} min="0" step="0.01" /></div>
            <div><label style={L}>{p.salePrice} ({getCurrencySymbol()})</label><input type="number" value={form.sale_price} onChange={e => setForm(p => ({ ...p, sale_price: e.target.value }))} style={I} min="0" step="0.01" /></div>
            <div><label style={L}>{p.minimumStock}</label><input type="number" value={form.min_stock} onChange={e => setForm(p => ({ ...p, min_stock: parseInt(e.target.value) || 0 }))} style={I} min="0" /></div>
            <div><label style={L}>{p.reorderQuantity}</label><input type="number" value={form.reorder_quantity} onChange={e => setForm(p => ({ ...p, reorder_quantity: e.target.value }))} style={I} min="0" /></div>
            <div style={{ gridColumn: "1/-1" }}><label style={L}>{p.description}</label><textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={3} style={{ ...I, resize: "vertical" }} /></div>
          </div>
          {error && <div style={{ marginTop: 14, padding: "10px 14px", background: t.accent.redBg, border: `1px solid ${t.accent.red}44`, borderRadius: 8, color: t.accent.red, fontSize: 13 }}>{error}</div>}
        </div>
        <div style={{ padding: "16px 28px", borderTop: `1px solid ${t.border.default}`, display: "flex", justifyContent: "flex-end", gap: 12, background: t.bg.header }}>
          <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 8, border: `1px solid ${t.border.input}`, background: "none", color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>{p.cancel}</button>
          <button onClick={handleSave} disabled={loading} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: loading ? t.bg.tag : "linear-gradient(135deg,#3b82f6,#6366f1)", color: "#fff", cursor: loading ? "default" : "pointer", fontSize: 13, fontWeight: 600 }}>{loading ? p.saving : p.save}</button>
        </div>
      </div>
    </div>
  );
};

// ─── ÜRÜN DOSYALARI SEKMESİ ──────────────────────────────────────────────────
const ProductFilesTab = ({ productCode, t }) => {
  const [files, setFiles]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory]   = useState("drawing");
  const [description, setDesc]    = useState("");
  const [msg, setMsg]             = useState(null);
  const fileInputRef              = useRef(null);

  const CATEGORIES = [
    { value:"drawing", label:p.drawingFile },
    { value:"pdf",     label:p.pdfDocument },
    { value:"image",   label:"🖼️ Resim" },
    { value:"other",   label:p.fileOther },
  ];

  const FILE_ICONS = {
    pdf:   "📄", image: "🖼️", cad: "📐",
    dwg:   "📐", dxf:  "📐", png: "🖼️",
    jpg:   "🖼️", jpeg: "🖼️", other: "📁",
    gcode: "⚙️", nc: "⚙️", stl: "🧊",
  };

  const formatSize = bytes => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024*1024) return (bytes/1024).toFixed(1) + " KB";
    return (bytes/1024/1024).toFixed(1) + " MB";
  };

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/product-cards/${productCode}/files`, { headers: headers() });
      if (res.ok) setFiles(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { if (productCode) fetchFiles(); }, [productCode]); // eslint-disable-line

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setMsg(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("file_category", category);
      formData.append("description", description);
      const res = await fetch(`${API}/product-cards/${productCode}/files`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: formData
      });
      if (res.ok) {
        setMsg({ type:"success", text:p.fileUploaded });
        setDesc("");
        fetchFiles();
      } else {
        const d = await res.json();
        setMsg({ type:"error", text:p.error + ": " + d.detail });
      }
    } catch { setMsg({ type:"error", text:p.connectionError }); }
    setUploading(false);
    e.target.value = "";
  };

  const handleDelete = async (fileId, fileName) => {
    if (!window.confirm(`"${fileName}" dosyasını silmek istediğinizden emin misiniz?`)) return;
    try {
      const res = await fetch(`${API}/product-cards/${productCode}/files/${fileId}`, {
        method: "DELETE", headers: headers()
      });
      if (res.ok) { setMsg({ type:"success", text:p.fileDeleted }); fetchFiles(); }
      else setMsg({ type:"error", text:p.deleteError });
    } catch { setMsg({ type:"error", text:p.connectionError }); }
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <div style={{ background:t.bg.metricCard, border:`1px solid ${t.border.default}`, borderRadius:12, padding:16 }}>
        <div style={{ fontSize:12, fontWeight:700, color:t.text.label, textTransform:"uppercase", marginBottom:14 }}>{p.uploadFile}</div>
        {msg && (
          <div style={{ padding:"8px 12px", borderRadius:8, marginBottom:12, fontSize:12, fontWeight:600,
            background: msg.type==="success" ? `${t.accent.green}18` : `${t.accent.red}18`,
            color: msg.type==="success" ? t.accent.green : t.accent.red,
            border:`1px solid ${msg.type==="success" ? t.accent.green : t.accent.red}44` }}>
            {msg.text}
          </div>
        )}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr auto", gap:12, alignItems:"flex-end" }}>
          <div>
            <div style={{ fontSize:11, color:t.text.muted, marginBottom:6 }}>{p.category}</div>
            <select value={category} onChange={e => setCategory(e.target.value)}
              style={{ width:"100%", padding:"10px 12px", background:t.bg.input, border:`1px solid ${t.border.input}`, borderRadius:8, color:t.text.primary, fontSize:13, outline:"none" }}>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize:11, color:t.text.muted, marginBottom:6 }}>{p.descriptionOptional}</div>
            <input type="text" value={description} onChange={e => setDesc(e.target.value)}
              placeholder={p.fileNotePlaceholder}
              style={{ width:"100%", padding:"10px 12px", background:t.bg.input, border:`1px solid ${t.border.input}`, borderRadius:8, color:t.text.primary, fontSize:13, outline:"none", boxSizing:"border-box" }} />
          </div>
          <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
            style={{ padding:"10px 20px", borderRadius:8, border:"none", background:"linear-gradient(135deg,#3b82f6,#6366f1)", color:"#fff", cursor:"pointer", fontSize:13, fontWeight:600, whiteSpace:"nowrap" }}>
            {uploading ? p.loading : p.selectFile}
          </button>
        </div>
        <input ref={fileInputRef} type="file" style={{ display:"none" }}
          accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.dwg,.dxf,.step,.stp,.iges,.igs,.stl,.obj,.3mf,.nc,.cnc,.gcode,.tap,.mpf,.doc,.docx,.xls,.xlsx"
          onChange={handleUpload} />
        <div style={{ fontSize:11, color:t.text.muted, marginTop:8 }}>
          Desteklenen: PDF, PNG, JPG, DWG, DXF, STEP, DOC, XLS · Max 20MB
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign:"center", padding:"40px 0", color:t.text.muted }}>{p.loading}</div>
      ) : files.length === 0 ? (
        <div style={{ textAlign:"center", padding:"40px 0", color:t.text.placeholder, fontSize:14 }}>
          Henüz dosya yüklenmemiş
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {files.map(f => {
            const icon = FILE_ICONS[f.file_type] || "📁";
            const catLabel = CATEGORIES.find(c => c.value === f.file_category)?.label || f.file_category;
            return (
              <div key={f.id} style={{ background:t.bg.metricCard, border:`1px solid ${t.border.default}`, borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"center", gap:12 }}>
                {f.file_type === "image" || ["jpg","jpeg","png","gif","webp"].includes(f.file_type) ? (
                  <img src={`${API}${f.file_path}`} alt={f.file_name}
                    style={{ width:48, height:48, objectFit:"cover", borderRadius:6, border:`1px solid ${t.border.subtle}` }} />
                ) : (
                  <span style={{ fontSize:32 }}>{icon}</span>
                )}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:t.text.primary, marginBottom:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{f.file_name}</div>
                  <div style={{ fontSize:11, color:t.text.muted }}>
                    {catLabel} · {formatSize(f.file_size)} · {f.uploaded_by} · {new Date(f.created_at).toLocaleDateString(getLocale())}
                    {f.description && <span> · {f.description}</span>}
                  </div>
                </div>
                <div style={{ display:"flex", gap:8, flexShrink:0 }}>
                  <a href={`${API}${f.file_path}`} target="_blank" rel="noreferrer"
                    style={{ padding:"6px 12px", borderRadius:6, border:`1px solid ${t.border.input}`, background:t.bg.toggle, color:t.text.secondary, textDecoration:"none", fontSize:11, fontWeight:600, cursor:"pointer" }}>
                    ⬇ {p.download}
                  </a>
                  {["admin","manager","production_manager"].includes(localStorage.getItem("role")) && (
                    <button onClick={() => handleDelete(f.id, f.file_name)}
                      style={{ padding:"6px 12px", borderRadius:6, border:`1px solid ${t.accent.red}44`, background:`${t.accent.red}11`, color:t.accent.red, fontSize:11, fontWeight:600, cursor:"pointer" }}>
                      🗑 {p.delete}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ProductDetailModal = ({ product, onClose, t, suppliers, machines, departments }) => {
  const p = getLang().productsPage;
  const [activeTab, setActiveTab] = useState("info");
  const [detail, setDetail] = useState(null);
  const [mrp, setMrp] = useState(null);
  const [mrpQty, setMrpQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showBomForm, setShowBomForm] = useState(false);
  const [showOutsourceForm, setShowOutsourceForm] = useState(false);
  const [showOpForm, setShowOpForm] = useState(false);
  const [bomForm, setBomForm] = useState({ material_name: "", material_code: "", material_type: "raw", quantity_required: "", unit: "adet", unit_cost: "", notes: "" });
  const [stockSuggestions, setStockSuggestions] = useState([]);
  const [showStockSuggest, setShowStockSuggest] = useState(false);
  const [outsourceForm, setOutsourceForm] = useState({ operation_name: "", operation_type: "", supplier_id: "", unit_cost: "", lead_time_days: "1", sequence_no: "1" });
  const [opForm, setOpForm] = useState({ operation_name: "", machine_id: "", machine_name: "", setup_time_min: "0", cycle_time_min: "", sequence_no: "1" });
  const [saving, setSaving] = useState(false);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/product-cards/${product.product_code}`, { headers: headers() });
      if (res.ok) setDetail(await res.json());
    } catch {}
    setLoading(false);
  }, [product.product_code]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const fetchMrp = async () => {
    try {
      const res = await fetch(`${API}/mrp/calculate/${product.product_code}?quantity=${mrpQty}`, { headers: headers() });
      if (res.ok) setMrp(await res.json());
    } catch {}
  };

  const addBom = async () => {
    if (!bomForm.material_name || !bomForm.quantity_required) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/bom`, {
        method: "POST", headers: headers(),
        body: JSON.stringify({ product_code: product.product_code, ...bomForm, quantity_required: parseFloat(bomForm.quantity_required), unit_cost: parseFloat(bomForm.unit_cost) || 0 }),
      });
      if (!res.ok) { alert((await res.json()).detail || p.error); setSaving(false); return; }
      setBomForm({ material_name: "", material_code: "", material_type: "raw", quantity_required: "", unit: "adet", unit_cost: "", notes: "" });
      setShowBomForm(false); fetchDetail();
    } catch (e) { alert(p.error + ": " + e.message); }
    setSaving(false);
  };

  const deleteBom = async (id) => { if (!window.confirm(p.deleteConfirm)) return; await fetch(`${API}/bom/${id}`, { method: "DELETE", headers: headers() }); fetchDetail(); };

  const addOutsource = async () => {
    if (!outsourceForm.operation_name) return;
    setSaving(true);
    try {
      await fetch(`${API}/outsource-operations`, { method: "POST", headers: headers(), body: JSON.stringify({ ...outsourceForm, product_code: product.product_code, unit_cost: parseFloat(outsourceForm.unit_cost) || 0, lead_time_days: parseInt(outsourceForm.lead_time_days) || 1, sequence_no: parseInt(outsourceForm.sequence_no) || 1, supplier_id: outsourceForm.supplier_id ? parseInt(outsourceForm.supplier_id) : null }) });
      setOutsourceForm({ operation_name: "", operation_type: "", supplier_id: "", unit_cost: "", lead_time_days: "1", sequence_no: "1" });
      setShowOutsourceForm(false); fetchDetail();
    } catch {}
    setSaving(false);
  };

  const deleteOutsource = async (id) => { if (!window.confirm(p.deleteConfirm)) return; await fetch(`${API}/outsource-operations/${id}`, { method: "DELETE", headers: headers() }); fetchDetail(); };

  const addOp = async () => {
    if (!opForm.operation_name) return;
    setSaving(true);
    try {
      await fetch(`${API}/product-operations`, { method: "POST", headers: headers(), body: JSON.stringify({ ...opForm, product_code: product.product_code, setup_time_min: parseFloat(opForm.setup_time_min) || 0, cycle_time_min: parseFloat(opForm.cycle_time_min) || 0, sequence_no: parseInt(opForm.sequence_no) || 1 }) });
      setOpForm({ operation_name: "", machine_id: "", machine_name: "", setup_time_min: "0", cycle_time_min: "", sequence_no: "1" });
      setShowOpForm(false); fetchDetail();
    } catch {}
    setSaving(false);
  };

  const deleteOp = async (id) => { if (!window.confirm(p.deleteConfirm)) return; await fetch(`${API}/product-operations/${id}`, { method: "DELETE", headers: headers() }); fetchDetail(); };

  const SI = { width: "100%", padding: "8px 10px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 8, color: t.text.primary, fontSize: 12, outline: "none", boxSizing: "border-box" };
  const SL = { display: "block", fontSize: 10, fontWeight: 700, color: t.text.label, textTransform: "uppercase", marginBottom: 4 };
  const addBtn = { padding: "7px 14px", borderRadius: 8, border: "none", background: "linear-gradient(135deg,#059669,#10b981)", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600 };
  const delBtn = { padding: "4px 10px", borderRadius: 6, border: `1px solid ${t.accent.red}44`, background: t.accent.redBg, color: t.accent.red, cursor: "pointer", fontSize: 11 };
  const typeCfg = TYPE_CFG[product.product_type] || TYPE_CFG.manufactured;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: t.bg.overlay, backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 20, width: "100%", maxWidth: 900, maxHeight: "92vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: t.shadow.modal }} onClick={e => e.stopPropagation()}>

        <div style={{ padding: "18px 28px", borderBottom: `1px solid ${t.border.default}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: t.bg.header }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: `${typeCfg.color}20`, border: `1px solid ${typeCfg.color}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>{typeCfg.icon}</div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: t.text.primary }}>{product.product_name}</div>
              <div style={{ fontSize: 13, color: t.text.muted }}>{product.product_code} · <span style={{ color: typeCfg.color }}>{typeCfg.label}</span></div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: t.text.muted, cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>

        <div style={{ display: "flex", gap: 2, borderBottom: `1px solid ${t.border.divider}`, padding: "0 28px", flexShrink: 0 }}>
          {[
            ["info", p.detailGeneralInfo],
            ["bom", `${p.detailRecipe} (${detail?.bom?.length || 0})`],
            ["outsource", `${p.detailOutsource} (${detail?.outsource?.length || 0})`],
            ["operations", `${p.detailOperations} (${detail?.operations?.length || 0})`],
            ["bom_tree", p.detailBomTree],
            ["mrp", p.detailMrpCost],
            ["files", p.detailFiles]
          ].map(([id, label]) => (
            <button key={id} onClick={() => setActiveTab(id)} style={{ padding: "10px 16px", background: "none", border: "none", cursor: "pointer", color: activeTab === id ? t.accent.blue : t.text.muted, fontSize: 12, fontWeight: 600, borderBottom: `2px solid ${activeTab === id ? t.accent.blue : "transparent"}`, whiteSpace: "nowrap" }}>{label}</button>
          ))}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: 40, color: t.text.muted }}>{p.loading}</div>
          ) : (
            <>
              {activeTab === "info" && detail && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 20 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 14, textTransform: "uppercase" }}>{p.productInformation}</div>
                    {[
                      [p.productCode, detail.product_code],
                      [p.productName, detail.product_name],
                      [p.type, typeCfg.label],
                      [p.category, detail.category || "—"],
                      [p.unit, detail.unit],
                      [p.drawingNo, detail.drawing_no || "—"],
                      [p.revision, detail.revision || "—"],
                      [p.weight, detail.weight_kg ? `${detail.weight_kg} kg` : "—"],
                      [p.material, detail.material || "—"],
                      [p.standardTime, detail.standard_time_min ? `${detail.standard_time_min} dk` : "—"]
                    ].map(([k,v]) => (
                      <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${t.border.subtle}`, fontSize: 13 }}>
                        <span style={{ color: t.text.label }}>{k}</span>
                        <span style={{ color: t.text.primary, fontWeight: 500 }}>{v}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 20 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 14, textTransform: "uppercase" }}>{p.costSummary}</div>
                    {[
                      [p.rawMaterialCost, formatMoney(detail.cost_breakdown?.material_cost || 0), "#10b981"],
                      [p.outsourceCost, formatMoney(detail.cost_breakdown?.outsource_cost || 0), "#a78bfa"],
                      [p.laborCost, formatMoney(detail.cost_breakdown?.operation_cost || 0), "#60a5fa"],
                      [p.totalCost, formatMoney(detail.cost_breakdown?.total_cost || 0), "#f59e0b"],
                      [p.salePrice, formatMoney(detail.sale_price || 0), "#00ff9d"],
                      [
                        p.profitMargin,
                        detail.sale_price && detail.cost_breakdown?.total_cost
                          ? `%${(((detail.sale_price - detail.cost_breakdown.total_cost) / detail.sale_price) * 100).toFixed(1)}`
                          : "—",
                        (detail.sale_price - detail.cost_breakdown?.total_cost) >= 0 ? "#00ff9d" : "#ef4444"
                      ]
                    ].map(([k,v,color]) => (
                      <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${t.border.subtle}`, fontSize: 13 }}>
                        <span style={{ color: t.text.label }}>{k}</span>
                        <span style={{ color, fontWeight: 700 }}>{v}</span>
                      </div>
                    ))}
                    {detail.description && <div style={{ marginTop: 14, padding: "10px 14px", background: t.bg.tag, borderRadius: 8, fontSize: 13, color: t.text.secondary }}>📝 {detail.description}</div>}
                  </div>
                </div>
              )}

              {activeTab === "bom_tree" && (
                <BomTree productCode={product.product_code} t={t} />
              )}

              {activeTab === "bom" && (
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: t.text.primary }}>{p.productRecipeTitle}</div>
                    <button onClick={() => setShowBomForm(!showBomForm)} style={addBtn}>{p.addMaterial}</button>
                  </div>
                  {showBomForm && (
                    <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 16, marginBottom: 16 }}>
                      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr auto", gap: 10, alignItems: "end" }}>
                        <div style={{ position: "relative" }}>
                          <label style={SL}>{p.materialName} *</label>
                          <input value={bomForm.material_name} onChange={async e => {
                            const val = e.target.value;
                            setBomForm(p => ({ ...p, material_name: val, material_code: "", unit_cost: "" }));

                            const q = normCode(val);
                            if (q.length < 1) {
                              setStockSuggestions([]);
                              setShowStockSuggest(false);
                              return;
                            }

                            let list = [];
                            try {
                              const r = await fetch(`${API}/product-cards`, { headers: headers() });
                              const d = await r.json();
                              list = Array.isArray(d) ? d : (d.products || d.items || d.product_cards || []);
                            } catch (err) {
                              console.error("BOM autocomplete fetch error", err);
                            }

                            if (!list.length) list = products || [];

                            const matches = list.filter(p => {
                              const code = normCode(p.product_code || p.code || "");
                              const name = normCode(p.product_name || p.name || "");
                              const mat = normCode(p.material || "");
                              return code.includes(q) || name.includes(q) || mat.includes(q);
                            }).slice(0, 20);

                            console.log("BOM autocomplete", val, list.length, matches.length, matches);
                            setStockSuggestions(matches);
                            setShowStockSuggest(true);
                          }} onFocus={async e => {
                            const val = e.target.value;
                            if (normCode(val).length >= 1) {
                              e.target.dispatchEvent(new Event("input", { bubbles: true }));
                            }
                          }} onBlur={() => setTimeout(() => setShowStockSuggest(false), 300)} style={SI} placeholder="SAC-001, sac001, sac 001..." autoComplete="off" />
                          {showStockSuggest && (
                            <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#1e2a3a", border: "1px solid #334155", borderRadius: 8, zIndex: 99999, maxHeight: 240, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.4)" }}>
                              {stockSuggestions.length === 0 && (
                                <div style={{ padding: "8px 12px", fontSize: 12, color: "#94a3b8" }}>{p.noMatch}</div>
                              )}
                              {stockSuggestions.map((s, i) => (
                                <div key={i} onMouseDown={() => { setBomForm(p => ({
                                  ...p,
                                  material_code: s.product_code || s.code || "",
                                  material_name: s.product_name || "",
                                  unit_cost: s.unit_cost || s.cost || 0,
                                  unit: s.unit || s.uom || "adet",
                                  material_type: s.product_type === "manufactured" ? "semi" : (s.product_type === "purchased" ? "purchased" : "raw")
                                })); setShowStockSuggest(false); }}
                                  style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13, color: "#e2e8f0", borderBottom: "1px solid #334155", display: "flex", justifyContent: "space-between" }}>
                                  <span>{s.product_code} — {s.product_name}</span>
                                  <span style={{ color: "#60a5fa", fontSize: 11 }}>{s.unit_cost ? `${formatMoney(s.unit_cost)}/${s.unit || "adet"}` : ""}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div><label style={SL}>{p.type}</label><select value={bomForm.material_type} onChange={e => setBomForm(p => ({ ...p, material_type: e.target.value }))} style={SI}><option value="raw">{p.raw}</option><option value="purchased">{p.purchasedMaterial}</option><option value="semi">{p.semiFinished}</option></select></div>
                        <div><label style={SL}>Miktar *</label><input type="number" value={bomForm.quantity_required} onChange={e => setBomForm(p => ({ ...p, quantity_required: e.target.value }))} style={SI} min="0" step="0.001" /></div>
                        <div><label style={SL}>{p.unit}</label><select value={bomForm.unit} onChange={e => setBomForm(p => ({ ...p, unit: e.target.value }))} style={SI}>{["adet","kg","mt","lt","m2","gr","mm"].map(u => <option key={u} value={u}>{u}</option>)}</select></div>
                        <div><label style={SL}>{p.unitPrice} ({getCurrencySymbol()})</label><input type="number" value={bomForm.unit_cost} onChange={e => setBomForm(p => ({ ...p, unit_cost: e.target.value }))} style={SI} min="0" step="0.01" /></div>
                        <button onClick={addBom} disabled={saving} style={addBtn}>✓</button>
                      </div>
                    </div>
                  )}
                  {!detail?.bom?.length ? (
                    <div style={{ textAlign: "center", padding: "40px 0", color: t.text.placeholder }}>{p.noMaterials}</div>
                  ) : (
                    <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflow: "hidden" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead><tr style={{ background: t.bg.theadRow }}>{[p.materialCode,p.material,p.type,p.quantity,p.unit,p.unitPrice,p.totalCost,""].map(h => <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontSize: 10, fontWeight: 700, color: t.text.label, textTransform: "uppercase" }}>{h}</th>)}</tr></thead>
                        <tbody>
                          {detail.bom.map((b, i) => {
                            const mc = MATERIAL_TYPE_CFG[b.material_type] || MATERIAL_TYPE_CFG.raw;
                            return (
                              <tr key={b.id} style={{ borderTop: `1px solid ${t.border.subtle}`, background: i % 2 === 0 ? t.bg.rowAlt : t.bg.row }}>
                                <td style={{ padding: "10px 12px", fontSize: 12, color: t.accent.blue, fontWeight: 600 }}>{b.material_code || "—"}</td>
                                <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 600, color: t.text.primary }}>{b.material_name}</td>
                                <td style={{ padding: "10px 12px" }}><span style={{ fontSize: 11, fontWeight: 600, color: mc.color, background: `${mc.color}18`, padding: "2px 8px", borderRadius: 20 }}>{p[mc.labelKey] || b.material_type}</span></td>
                                <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: t.accent.blue }}>{b.quantity_required}</td>
                                <td style={{ padding: "10px 12px", fontSize: 12, color: t.text.muted }}>{b.unit}</td>
                                <td style={{ padding: "10px 12px", fontSize: 12, color: t.text.secondary }}>{formatMoney(b.unit_cost || 0)}</td>
                                <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: "#10b981" }}>{formatMoney(parseFloat(b.quantity_required || 0) * parseFloat(b.unit_cost || 0))}</td>
                                <td style={{ padding: "10px 12px" }}><button onClick={() => deleteBom(b.id)} style={delBtn}>{p.delete}</button></td>
                              </tr>
                            );
                          })}
                          <tr style={{ background: t.bg.theadRow }}>
                            <td colSpan={6} style={{ padding: "10px 12px", fontSize: 12, fontWeight: 700, color: t.text.label, textAlign: "right" }}>TOPLAM:</td>
                            <td style={{ padding: "10px 12px", fontSize: 14, fontWeight: 800, color: "#10b981" }}>{formatMoney(detail.bom.reduce((sum, b) => sum + parseFloat(b.quantity_required || 0) * parseFloat(b.unit_cost || 0), 0))}</td>
                            <td />
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "outsource" && (
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: t.text.primary }}>{p.outsourceTitle}</div>
                    <button onClick={() => setShowOutsourceForm(!showOutsourceForm)} style={addBtn}>{p.addOutsource}</button>
                  </div>
                  {showOutsourceForm && (
                    <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 16, marginBottom: 16 }}>
                      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr auto", gap: 10, alignItems: "end" }}>
                        <div><label style={SL}>{p.operationNameRequired}</label><input value={outsourceForm.operation_name} onChange={e => setOutsourceForm(p => ({ ...p, operation_name: e.target.value }))} style={SI} placeholder={p.outsourceOperationPlaceholder} /></div>
                        <div><label style={SL}>{p.type}</label><select value={outsourceForm.operation_type} onChange={e => setOutsourceForm(p => ({ ...p, operation_type: e.target.value }))} style={SI}><option value="">{p.select}</option>{["Kumlama","Galvaniz","Boyama","Kaplama","Dövme","Isıl İşlem","Taşlama","Nikel Kaplama","Sertleştirme","Diğer"].map(o => <option key={o} value={o}>{o}</option>)}</select></div>
                        <div><label style={SL}>{p.supplier}</label><select value={outsourceForm.supplier_id} onChange={e => setOutsourceForm(p => ({ ...p, supplier_id: e.target.value }))} style={SI}><option value="">{p.select}</option>{suppliers.map(s => <option key={s.id} value={s.id}>{s.name || s.supplier_name}</option>)}</select></div>
                        <div><label style={SL}>{p.unitCost} ({getCurrencySymbol()})</label><input type="number" value={outsourceForm.unit_cost} onChange={e => setOutsourceForm(p => ({ ...p, unit_cost: e.target.value }))} style={SI} min="0" step="0.01" /></div>
                        <div><label style={SL}>{p.leadTimeDays}</label><input type="number" value={outsourceForm.lead_time_days} onChange={e => setOutsourceForm(p => ({ ...p, lead_time_days: e.target.value }))} style={SI} min="1" /></div>
                        <button onClick={addOutsource} disabled={saving} style={addBtn}>✓</button>
                      </div>
                    </div>
                  )}
                  {!detail?.outsource?.length ? (
                    <div style={{ textAlign: "center", padding: "40px 0", color: t.text.placeholder }}>{p.noOutsource}</div>
                  ) : (
                    <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflow: "hidden" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead><tr style={{ background: t.bg.theadRow }}>{[p.sequence,p.operation,p.type,p.supplier,p.unitCost,p.leadTime,""].map(h => <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontSize: 10, fontWeight: 700, color: t.text.label, textTransform: "uppercase" }}>{h}</th>)}</tr></thead>
                        <tbody>
                          {detail.outsource.map((o, i) => (
                            <tr key={o.id} style={{ borderTop: `1px solid ${t.border.subtle}`, background: i % 2 === 0 ? t.bg.rowAlt : t.bg.row }}>
                              <td style={{ padding: "10px 12px", fontSize: 12, color: t.text.muted }}>{o.sequence_no}</td>
                              <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: t.text.primary }}>{o.operation_name}</td>
                              <td style={{ padding: "10px 12px", fontSize: 12, color: "#a78bfa" }}>{o.operation_type || "—"}</td>
                              <td style={{ padding: "10px 12px", fontSize: 12, color: t.text.secondary }}>{o.supplier_name || "—"}</td>
                              <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: "#a78bfa" }}>{formatMoney(o.unit_cost || 0)}</td>
                              <td style={{ padding: "10px 12px", fontSize: 12, color: t.text.muted }}>{o.lead_time_days} gün</td>
                              <td style={{ padding: "10px 12px" }}><button onClick={() => deleteOutsource(o.id)} style={delBtn}>{p.delete}</button></td>
                            </tr>
                          ))}
                          <tr style={{ background: t.bg.theadRow }}>
                            <td colSpan={4} style={{ padding: "10px 12px", fontSize: 12, fontWeight: 700, color: t.text.label, textAlign: "right" }}>TOPLAM FASON:</td>
                            <td style={{ padding: "10px 12px", fontSize: 14, fontWeight: 800, color: "#a78bfa" }}>{formatMoney(detail.outsource.reduce((sum, o) => sum + parseFloat(o.unit_cost || 0), 0))}</td>
                            <td colSpan={2} />
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "operations" && (
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: t.text.primary }}>{p.productionOperationsTitle}</div>
                    <button onClick={() => setShowOpForm(!showOpForm)} style={addBtn}>{p.addOperation}</button>
                  </div>
                  {showOpForm && (
                    <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 16, marginBottom: 16 }}>
                      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr auto", gap: 10, alignItems: "end" }}>
                        <div><label style={SL}>{p.operationName} *</label><input value={opForm.operation_name} onChange={e => setOpForm(p => ({ ...p, operation_name: e.target.value }))} style={SI} placeholder={p.operationPlaceholder} /></div>
                        <div><label style={SL}>{p.department}</label><select value={opForm.department || ""} onChange={e => setOpForm(p => ({ ...p, department: e.target.value }))} style={SI}><option value="">{p.select}</option>{(departments || []).map(d => <option key={d} value={d}>{d}</option>)}</select></div>
                        <div><label style={SL}>{p.machine}</label><select value={opForm.machine_id} onChange={e => { const m = machines.find(x => x.machine_id === e.target.value); setOpForm(p => ({ ...p, machine_id: e.target.value, machine_name: m?.machine_name || "" })); }} style={SI}><option value="">{p.select}</option>{machines.map(m => <option key={m.machine_id} value={m.machine_id}>{m.machine_name || m.machine_id}</option>)}</select></div>
                        <div><label style={SL}>{p.setupMinutes}</label><input type="number" value={opForm.setup_time_min} onChange={e => setOpForm(p => ({ ...p, setup_time_min: e.target.value }))} style={SI} min="0" step="0.1" /></div>
                        <div><label style={SL}>{p.cycleMinutes}</label><input type="number" value={opForm.cycle_time_min} onChange={e => setOpForm(p => ({ ...p, cycle_time_min: e.target.value }))} style={SI} min="0" step="0.1" /></div>
                        <div><label style={SL}>{p.sequenceNo}</label><input type="number" value={opForm.sequence_no} onChange={e => setOpForm(p => ({ ...p, sequence_no: e.target.value }))} style={SI} min="1" /></div>
                        <button onClick={addOp} disabled={saving} style={addBtn}>✓</button>
                      </div>
                    </div>
                  )}
                  {!detail?.operations?.length ? (
                    <div style={{ textAlign: "center", padding: "40px 0", color: t.text.placeholder }}>{p.noOperations}</div>
                  ) : (
                    <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflow: "hidden" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead><tr style={{ background: t.bg.theadRow }}>{[p.sequence,p.detailOperations.replace("⚙️ ",""),p.machine,p.setup,p.cycle,p.totalCost,p.cost,""].map(h => <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontSize: 10, fontWeight: 700, color: t.text.label, textTransform: "uppercase" }}>{h}</th>)}</tr></thead>
                        <tbody>
                          {detail.operations.map((op, i) => {
                            const total = (parseFloat(op.setup_time_min) || 0) + (parseFloat(op.cycle_time_min) || 0);
                            return (
                              <tr key={op.id} style={{ borderTop: `1px solid ${t.border.subtle}`, background: i % 2 === 0 ? t.bg.rowAlt : t.bg.row }}>
                                <td style={{ padding: "10px 12px", fontSize: 12, color: t.text.muted }}>{op.sequence_no}</td>
                                <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: t.text.primary }}>{op.operation_name}</td>
                                <td style={{ padding: "10px 12px", fontSize: 12, color: t.accent.blue }}>{op.machine_name || op.machine_id || "—"}</td>
                                <td style={{ padding: "10px 12px", fontSize: 12, color: t.text.secondary }}>{op.setup_time_min} dk</td>
                                <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: t.accent.blue }}>{op.cycle_time_min} dk</td>
                                <td style={{ padding: "10px 12px", fontSize: 12, color: t.text.muted }}>{total.toFixed(1)} dk</td>
                                <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: "#60a5fa" }}>{formatMoney(total / 60 * 50)}</td>
                                <td style={{ padding: "10px 12px" }}><button onClick={() => deleteOp(op.id)} style={delBtn}>{p.delete}</button></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "mrp" && (
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: t.text.primary }}>{p.mrpCalculation}</div>
                    <input type="number" value={mrpQty} onChange={e => setMrpQty(e.target.value)} style={{ width: 80, padding: "8px 10px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none" }} min="1" />
                    <span style={{ fontSize: 13, color: t.text.muted }}>{p.forPieces}</span>
                    <button onClick={fetchMrp} style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: "linear-gradient(135deg,#3b82f6,#6366f1)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>🔍 {p.calculate}</button>
                  </div>
                  {mrp ? (
                    <>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 20 }}>
                        {[
                          { label: p.rawMaterialCost, value: mrp.cost_breakdown.material_cost, color: "#10b981" },
                          { label: p.outsourceCost, value: mrp.cost_breakdown.outsource_cost, color: "#a78bfa" },
                          { label: p.laborSsi, value: mrp.cost_breakdown.labor_cost, color: "#60a5fa" },
                          { label: "Enerji", value: mrp.cost_breakdown.energy_cost, color: "#fbbf24" },
                          { label: `Fire (%${mrp.cost_breakdown.fire_rate})`, value: mrp.cost_breakdown.fire_cost, color: "#f87171" },
                          { label: `Genel Gider (%${mrp.cost_breakdown.overhead_rate})`, value: mrp.cost_breakdown.overhead_cost, color: "#94a3b8" },
                          { label: p.unitCostUpper, value: mrp.cost_breakdown.unit_cost, color: "#f59e0b" },
                          { label: "TOPLAM", value: mrp.cost_breakdown.total_cost, color: "#00ff9d" },
                        ].map(({ label, value, color }) => (
                          <div key={label} style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 12, padding: "14px 16px", textAlign: "center" }}>
                            <div style={{ fontSize: 10, color: t.text.muted, textTransform: "uppercase", marginBottom: 6 }}>{label}</div>
                            <div style={{ fontSize: 18, fontWeight: 800, color }}>{formatMoney(value || 0)}</div>
                          </div>
                        ))}
                      </div>
                      {mrp.has_shortage && (
                        <div style={{ padding: "10px 16px", background: t.accent.redBg, border: `1px solid ${t.accent.red}44`, borderRadius: 10, color: t.accent.red, fontSize: 13, fontWeight: 600, marginBottom: 16 }}>
                          {p.shortageWarning}
                        </div>
                      )}
                      <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflow: "hidden" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                          <thead><tr style={{ background: t.bg.theadRow }}>{[p.material,p.unitQuantity,p.totalRequired,p.inStock,p.shortage,p.unitPrice,p.totalCost,p.status].map(h => <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontSize: 10, fontWeight: 700, color: t.text.label, textTransform: "uppercase" }}>{h}</th>)}</tr></thead>
                          <tbody>
                            {mrp.materials.map((m, i) => (
                              <tr key={i} style={{ borderTop: `1px solid ${t.border.subtle}`, background: m.status === "shortage" ? `${t.accent.red}08` : i % 2 === 0 ? t.bg.rowAlt : t.bg.row }}>
                                <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 600, color: t.text.primary }}>{m.material_name}</td>
                                <td style={{ padding: "10px 12px", fontSize: 12, color: t.text.muted }}>{m.required_per_unit} {m.unit}</td>
                                <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: t.accent.blue }}>{m.total_required} {m.unit}</td>
                                <td style={{ padding: "10px 12px", fontSize: 13, color: m.available >= m.total_required ? "#10b981" : t.accent.yellow }}>{m.available}</td>
                                <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: m.shortage > 0 ? t.accent.red : "#10b981" }}>{m.shortage > 0 ? `⚠️ ${m.shortage}` : "✓"}</td>
                                <td style={{ padding: "10px 12px", fontSize: 12, color: t.text.secondary }}>{formatMoney(m.unit_price || 0)}</td>
                                <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: "#10b981" }}>{formatMoney(m.total_cost || 0)}</td>
                                <td style={{ padding: "10px 12px" }}><span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: m.status === "ok" ? "rgba(16,185,129,0.12)" : "rgba(248,113,113,0.12)", color: m.status === "ok" ? "#10b981" : t.accent.red }}>{m.status === "ok" ? p.ready : p.missing}</span></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <div style={{ textAlign: "center", padding: "40px 0", color: t.text.placeholder, fontSize: 14 }}>{p.enterQtyCalculate}</div>
                  )}
                </div>
              )}

              {activeTab === "files" && (
                <ProductFilesTab productCode={detail?.product_code} t={t} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default function Products() {
  const { t: lang } = useLanguage();
  const p = lang.productsPage;
  const { t } = useTheme();
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [machines, setMachines] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState(localStorage.getItem("productViewMode") || "list");
  const [filterType, setFilterType] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editProduct, setEditProduct] = useState(null);
  const [showTemplateMenu, setShowTemplateMenu] = useState(false);
  const [showImportMenu, setShowImportMenu] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        fetch(`${API}/product-cards`, { headers: headers() }),
        fetch(`${API}/suppliers`, { headers: headers() }),
        fetch(`${API}/machines/list`, { headers: headers() }),
        fetch(`${API}/hr/employees`, { headers: headers() }),
      ]);
      if (r1.ok) { const d = await r1.json(); setProducts(Array.isArray(d) ? d : []); }
      if (r2.ok) { const d = await r2.json(); setSuppliers(Array.isArray(d) ? d : (d.suppliers || [])); }
      if (r3.ok) { const d = await r3.json(); setMachines(d.machines || []); }
      if (r4.ok) { const d = await r4.json(); const emps = Array.isArray(d) ? d : (d.employees || []); setDepartments([...new Set(emps.map(e => e.department).filter(Boolean))].sort()); }
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleImport = async (endpoint, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await fetch(`${API}/${endpoint}`, { method: "POST", headers: { Authorization: `Bearer ${token()}` }, body: fd });
      const result = await res.json();
      fetchData();
      alert(`✅ ${result.success} kayıt yüklendi.${result.errors?.length > 0 ? ` ⚠️ ${result.errors.length} hata.` : ""}`);
    } catch (e) { alert(p.error + ": " + e.message); }
  };

  const downloadFile = async (url, filename) => {
    const res = await fetch(url, { headers: headers() });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const filtered = products.filter(p => {
    const s = search.toLowerCase().replace(/[-\s]/g, "");
    const matchSearch = !search ||
      (p.product_name || "").toLowerCase().includes(search.toLowerCase()) ||
      (p.product_code || "").toLowerCase().replace(/[-\s]/g, "").includes(s) ||
      (p.category || "").toLowerCase().includes(search.toLowerCase());
    return matchSearch && (filterType === "all" || p.product_type === filterType);
  });

  const pagedProducts = filtered.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const stats = {
    total: products.length,
    manufactured: products.filter(p => p.product_type === "manufactured").length,
    purchased: products.filter(p => p.product_type === "purchased").length,
    set: products.filter(p => p.product_type === "set").length,
    withBom: products.filter(p => p.bom_count > 0).length,
  };

  const ss = { padding: "10px 14px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 10, color: t.text.primary, fontSize: 13, outline: "none", cursor: "pointer" };
  const mi = { display: "block", padding: "9px 14px", color: t.text.primary, borderRadius: 8, fontSize: 13, cursor: "pointer" };

  return (
    <div style={{ minHeight: "100vh", background: t.bg.page, color: t.text.primary, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <style>{`@keyframes sf-spin { to { transform: rotate(360deg) } }`}</style>
      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "28px 24px" }}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: t.text.primary, margin: 0 }}>{p.title}</h1>
            <p style={{ color: t.text.muted, margin: "6px 0 0", fontSize: 14 }}>{p.mainSubtitle}</p>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <button onClick={fetchData} style={{ padding: "10px 16px", borderRadius: 10, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>🔄</button>

            <div style={{ position: "relative" }}>
              <button onClick={() => { setShowTemplateMenu(!showTemplateMenu); setShowImportMenu(false); }} style={{ padding: "10px 16px", borderRadius: 10, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>{p.template}</button>
              {showTemplateMenu && (
                <div style={{ position: "absolute", top: "110%", right: 0, zIndex: 200, background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 12, padding: 8, minWidth: 220, boxShadow: t.shadow.modal }}>
                  {[["products",p.templateProducts],["bom",p.templateBom],["outsource",p.templateOutsource]].map(([type, label]) => (
                    <div key={type} onClick={() => { setShowTemplateMenu(false); downloadFile(`${API}/product-cards/template?type=${type}`, `${type}_sablonu.xlsx`); }} style={mi} onMouseEnter={e => e.currentTarget.style.background = t.accent.blueBg} onMouseLeave={e => e.currentTarget.style.background = "none"}>{label}</div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ position: "relative" }}>
              <button onClick={() => { setShowImportMenu(!showImportMenu); setShowTemplateMenu(false); }} style={{ padding: "10px 16px", borderRadius: 10, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>{p.import}</button>
              {showImportMenu && (
                <div style={{ position: "absolute", top: "110%", right: 0, zIndex: 200, background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 12, padding: 8, minWidth: 220, boxShadow: t.shadow.modal }}>
                  {[["product-cards/import",p.templateProducts],["bom/import",p.templateBom],["outsource-operations/import",p.templateOutsource]].map(([endpoint, label]) => (
                    <label key={endpoint} style={mi} onMouseEnter={e => e.currentTarget.style.background = t.accent.blueBg} onMouseLeave={e => e.currentTarget.style.background = "none"}>
                      {label}
                      <input type="file" accept=".xlsx,.csv" style={{ display: "none" }} onChange={e => { handleImport(endpoint, e.target.files[0]); setShowImportMenu(false); }} />
                    </label>
                  ))}
                </div>
              )}
            </div>

            <button onClick={() => downloadFile(`${API}/product-cards/export`, "urun_kartlari.xlsx")} style={{ padding: "10px 16px", borderRadius: 10, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>{p.export}</button>
            <button onClick={() => setShowNew(true)} style={{ padding: "10px 20px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#3b82f6,#6366f1)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>{p.newProductCard}</button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
          {[
            { label: p.totalProducts, value: stats.total, color: t.accent.blue, icon: "📦" },
            { label: p.ownProduction, value: stats.manufactured, color: "#3b82f6", icon: "🏭" },
            { label: p.externallyPurchased, value: stats.purchased, color: "#a78bfa", icon: "🛒" },
            { label: p.setKit, value: stats.set, color: "#f59e0b", icon: "📦" },
            { label: p.bomDefined, value: stats.withBom, color: "#10b981", icon: "🔩" },
          ].map(s => (
            <div key={s.label} style={{ flex: 1, minWidth: 120, background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: "14px 16px", display: "flex", alignItems: "center", gap: 10, boxShadow: t.shadow.card }}>
              <span style={{ fontSize: 20 }}>{s.icon}</span>
              <div>
                <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 10, color: t.text.muted, fontWeight: 600 }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
          <input placeholder={p.searchPlaceholder} value={search} onChange={e => setSearch(e.target.value)} style={{ ...ss, flex: 1, minWidth: 220 }} />
          <select value={filterType} onChange={e => setFilterType(e.target.value)} style={ss}>
            <option value="all">{p.allTypes}</option>
            <option value="manufactured">🏭 {p.ownProduction}</option>
            <option value="purchased">🛒 {p.externallyPurchased}</option>
            <option value="set">📦 Set/Kit</option>
          </select>
          <div style={{ display: "flex", gap: 4 }}>
            <button onClick={() => { setViewMode("grid"); localStorage.setItem("productViewMode", "grid"); }} style={{ padding: "10px 12px", borderRadius: 10, border: `1px solid ${t.border.input}`, background: viewMode === "grid" ? t.accent.blueBg : t.bg.toggle, color: viewMode === "grid" ? t.accent.blue : t.text.muted, cursor: "pointer", fontSize: 14 }}>⊞</button>
            <button onClick={() => { setViewMode("list"); localStorage.setItem("productViewMode", "list"); }} style={{ padding: "10px 12px", borderRadius: 10, border: `1px solid ${t.border.input}`, background: viewMode === "list" ? t.accent.blueBg : t.bg.toggle, color: viewMode === "list" ? t.accent.blue : t.text.muted, cursor: "pointer", fontSize: 14 }}>☰</button>
          </div>
        </div>

        {loading ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 0", gap: 12 }}>
            <div style={{ width: 28, height: 28, border: `2px solid ${t.border.default}`, borderTopColor: t.accent.blue, borderRadius: "50%", animation: "sf-spin 0.8s linear infinite" }} />
            <span style={{ color: t.text.muted }}>{p.loading}</span>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "80px 0", color: t.text.placeholder }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📦</div>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>{products.length === 0 ? p.noProducts : p.noMatch}</div>
            <div style={{ fontSize: 13 }}>{p.clickNewProductHint}</div>
          </div>
        ) : viewMode === "grid" ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 }}>
            {pagedProducts.map(p => {
              const tc = TYPE_CFG[p.product_type] || TYPE_CFG.manufactured;
              return (
                <div key={p.id} style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 16, padding: "18px 20px", boxShadow: t.shadow.card, cursor: "pointer", transition: "all 0.2s" }}
                  onClick={() => setSelected(p)}
                  onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
                  onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: tc.color, marginBottom: 4 }}>{tc.icon} {tc.label}</div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: t.text.primary }}>{p.product_name}</div>
                      <div style={{ fontSize: 12, color: t.accent.blue, fontWeight: 600 }}>{p.product_code}</div>
                    </div>
                    <button onClick={e => { e.stopPropagation(); setEditProduct(p); }} style={{ padding: "5px 10px", borderRadius: 8, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.muted, cursor: "pointer", fontSize: 11 }}>✏️</button>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                    {p.category && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: t.bg.tag, color: t.text.muted }}>{p.category}</span>}
                    {p.drawing_no && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: t.bg.tag, color: t.text.muted }}>📐 {p.drawing_no}</span>}
                    {p.material && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: t.bg.tag, color: t.text.muted }}>{p.material}</span>}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                    {[{ v: p.bom_count || 0, l: "BOM", c: "#10b981" }, { v: p.outsource_count || 0, l: "Fason", c: "#a78bfa" }, { v: formatMoney(p.sale_price || 0, { maximumFractionDigits: 0 }), l: getLang().productsPage.sales, c: "#f59e0b" }].map(({ v, l, c }) => (
                      <div key={l} style={{ textAlign: "center", padding: "8px", background: t.bg.metricCard, borderRadius: 10 }}>
                        <div style={{ fontSize: 16, fontWeight: 800, color: c }}>{v}</div>
                        <div style={{ fontSize: 9, color: t.text.muted }}>{l}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflow: "hidden", boxShadow: t.shadow.card }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: t.bg.theadRow }}>
                  {[p.productCode,p.productName,p.type,p.category,p.unit,p.bom,p.outsource,p.salePrice,""].map(h => (
                    <th key={h} style={{ padding: "12px 14px", textAlign: "left", fontSize: 10, fontWeight: 700, color: t.text.label, textTransform: "uppercase" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pagedProducts.map((p, i) => {
                  const tc = TYPE_CFG[p.product_type] || TYPE_CFG.manufactured;
                  return (
                    <tr key={p.id}
                      style={{ borderTop: `1px solid ${t.border.subtle}`, background: i % 2 === 0 ? t.bg.rowAlt : t.bg.row, cursor: "pointer" }}
                      onClick={() => setSelected(p)}
                      onMouseEnter={e => e.currentTarget.style.background = t.accent.blueBg}
                      onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? t.bg.rowAlt : t.bg.row}>
                      <td style={{ padding: "12px 14px", fontSize: 13, fontWeight: 700, color: t.accent.blue }}>{p.product_code}</td>
                      <td style={{ padding: "12px 14px", fontSize: 13, fontWeight: 600, color: t.text.primary }}>{p.product_name}</td>
                      <td style={{ padding: "12px 14px" }}><span style={{ fontSize: 11, fontWeight: 600, color: tc.color, background: `${tc.color}18`, padding: "2px 8px", borderRadius: 20 }}>{tc.icon} {tc.label}</span></td>
                      <td style={{ padding: "12px 14px", fontSize: 12, color: t.text.muted }}>{p.category || "—"}</td>
                      <td style={{ padding: "12px 14px", fontSize: 12, color: t.text.muted }}>{p.unit}</td>
                      <td style={{ padding: "12px 14px", fontSize: 13, fontWeight: 700, color: "#10b981" }}>{p.bom_count || 0}</td>
                      <td style={{ padding: "12px 14px", fontSize: 13, fontWeight: 700, color: "#a78bfa" }}>{p.outsource_count || 0}</td>
                      <td style={{ padding: "12px 14px", fontSize: 13, fontWeight: 700, color: "#f59e0b" }}>{formatMoney(p.sale_price || 0)}</td>
                      <td style={{ padding: "12px 14px" }}><button onClick={e => { e.stopPropagation(); setEditProduct(p); }} style={{ padding: "4px 10px", borderRadius: 6, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.muted, cursor: "pointer", fontSize: 11 }}>✏️</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showNew && <ProductFormModal t={t} products={products} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); fetchData(); }} />}
      {editProduct && <ProductFormModal existing={editProduct} t={t} products={products} onClose={() => setEditProduct(null)} onSaved={() => { setEditProduct(null); fetchData(); }} />}
      {!loading && filtered.length > 0 && (
        <PaginationBar
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalRecords={filtered.length}
          label={p.productLabel}
          t={t}
        />
      )}

      {selected && <ProductDetailModal product={selected} t={t} suppliers={suppliers} machines={machines} departments={departments} onClose={() => setSelected(null)} />}
    </div>
  );
}
